import React from 'react';
import { calculateYearsToEducationStage } from '../../utils/educationCalculations';
import './EducationTimeline.css';

const EducationTimeline = ({ requiredSavings, currentAge, progressTracker }) => {
  const currentYear = new Date().getFullYear();
  
  const stageInfo = {
    elementary: { name: '小学校', icon: '🎒', color: '#4caf50' },
    junior: { name: '中学校', icon: '📚', color: '#2196f3' },
    senior: { name: '高校', icon: '🎓', color: '#ff9800' },
    university: { name: '大学', icon: '🏛️', color: '#9c27b0' }
  };

  return (
    <div className="education-timeline">
      <h3>教育段階タイムライン</h3>
      <p className="timeline-description">
        お子様の成長に合わせた教育費の準備スケジュールです
      </p>
      
      <div className="timeline-container">
        <div className="timeline-line"></div>
        
        {requiredSavings.map((stage, index) => {
          const info = stageInfo[stage.stage];
          const targetYear = currentYear + stage.yearsToStage;
          const isUpcoming = stage.yearsToStage <= 2 && stage.yearsToStage > 0;
          const isPassed = stage.yearsToStage === 0;
          
          return (
            <div 
              key={stage.stage} 
              className={`timeline-item ${isPassed ? 'passed' : ''} ${isUpcoming ? 'upcoming' : ''}`}
            >
              <div 
                className="timeline-marker"
                style={{ backgroundColor: info.color }}
              >
                <span className="stage-icon">{info.icon}</span>
              </div>
              
              <div className="timeline-content">
                <div className="stage-header">
                  <h4 className="stage-name">{info.name}</h4>
                  <div className="stage-timing">
                    {isPassed ? (
                      <span className="timing-current">現在</span>
                    ) : (
                      <>
                        <span className="timing-years">{stage.yearsToStage}年後</span>
                        <span className="timing-year">({targetYear}年)</span>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="stage-details">
                  <div className="detail-row">
                    <span className="detail-label">この段階の費用</span>
                    <span className="detail-value cost">
                      {(stage.stageCost / 10000).toFixed(1)}万円
                    </span>
                  </div>
                  
                  <div className="detail-row">
                    <span className="detail-label">累積必要額</span>
                    <span className="detail-value cumulative">
                      {(stage.cumulativeCost / 10000).toFixed(1)}万円
                    </span>
                  </div>
                  
                  {stage.yearsToStage > 0 && (
                    <div className="detail-row">
                      <span className="detail-label">月間目標貯蓄額</span>
                      <span className="detail-value target">
                        {Math.round(stage.cumulativeCost / (stage.yearsToStage * 12)).toLocaleString()}円
                      </span>
                    </div>
                  )}
                </div>
                
                {/* 進捗バー */}
                {progressTracker && (
                  <div className="progress-section">
                    <div className="progress-header">
                      <span className="progress-label">貯蓄進捗</span>
                      <span className="progress-percentage">
                        {Math.min(100, Math.round((progressTracker.currentAmount / stage.cumulativeCost) * 100))}%
                      </span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill"
                        style={{ 
                          width: `${Math.min(100, (progressTracker.currentAmount / stage.cumulativeCost) * 100)}%`,
                          backgroundColor: info.color
                        }}
                      ></div>
                    </div>
                    <div className="progress-amounts">
                      <span className="current-amount">
                        現在: {(progressTracker.currentAmount / 10000).toFixed(1)}万円
                      </span>
                      <span className="target-amount">
                        目標: {(stage.cumulativeCost / 10000).toFixed(1)}万円
                      </span>
                    </div>
                  </div>
                )}
                
                {/* アラート */}
                {isUpcoming && (
                  <div className="stage-alert upcoming-alert">
                    <span className="alert-icon">⏰</span>
                    <span className="alert-text">
                      {stage.yearsToStage}年後に迫っています！準備を急ぎましょう
                    </span>
                  </div>
                )}
                
                {isPassed && (
                  <div className="stage-alert current-alert">
                    <span className="alert-icon">🎯</span>
                    <span className="alert-text">
                      現在この段階です
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* サマリー情報 */}
      <div className="timeline-summary">
        <div className="summary-item">
          <span className="summary-label">お子様の現在年齢</span>
          <span className="summary-value">{currentAge}歳</span>
        </div>
        
        <div className="summary-item">
          <span className="summary-label">大学入学まで</span>
          <span className="summary-value">
            {calculateYearsToEducationStage(currentAge, 'university')}年
          </span>
        </div>
        
        <div className="summary-item">
          <span className="summary-label">教育費総額</span>
          <span className="summary-value total">
            {requiredSavings.length > 0 ? 
              (requiredSavings[requiredSavings.length - 1].cumulativeCost / 10000).toFixed(1) : 0}万円
          </span>
        </div>
        
        {progressTracker && (
          <div className="summary-item">
            <span className="summary-label">現在の貯蓄額</span>
            <span className="summary-value current">
              {(progressTracker.currentAmount / 10000).toFixed(1)}万円
            </span>
          </div>
        )}
      </div>
      
      {/* 重要なマイルストーン */}
      <div className="important-milestones">
        <h4>重要なマイルストーン</h4>
        <div className="milestones-grid">
          <div className="milestone-card">
            <div className="milestone-icon">🎂</div>
            <div className="milestone-content">
              <h5>12歳まで</h5>
              <p>ジュニアNISAの積立完了推奨時期</p>
            </div>
          </div>
          
          <div className="milestone-card">
            <div className="milestone-icon">🎓</div>
            <div className="milestone-content">
              <h5>18歳</h5>
              <p>ジュニアNISA引き出し可能・大学入学</p>
            </div>
          </div>
          
          <div className="milestone-card">
            <div className="milestone-icon">⚠️</div>
            <div className="milestone-content">
              <h5>30歳まで</h5>
              <p>教育資金贈与制度の使用期限</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EducationTimeline;
